import cx from 'clsx';
import React from 'react';

import AssetLayer from '@/components/AssetLayer/AssetLayer';
import {
  JackpotLayerGameTile,
  LiveTableLayer,
  QuicksilverTileOutfitType,
  SashLayer,
} from '@/components/game-tile';
import AnimatedAssetLayer from '@/components/game-tile/AnimatedAssetLayer/AnimatedAssetLayer';
import GameTile from '@/components/game-tile/GameTile';
import LayersWrapper from '@/components/game-tile/LayersWrapper';
import GameLink from '@/components/GameLink';
import { Info } from '@/components/icons';
import IntersectionObserverWrapper from '@/components/IntersectionObserverWrapper';
import { ShouldPreventDefaultForClick } from '@/components/LaunchStrategiesProvider/utils';
import { SMALL_OUTFIT } from '@/components/modular-grid/constants';
import GhostTile from '@/components/modular-grid/GhostTile';
import { GameClickProps, GameTileProps } from '@/components/modular-grid/types';
import { prepareGameInfoTooltip } from '@/components/modular-grid/utils';
import { UserSessionContext } from '@/components/UserSessionProvider';
import { IMAGE_SIZES } from '@/model/algolia/constants';
import { transformImage } from '@/model/algolia/utils';
import {
  CategoryFinancialWebAttributes,
  CategoryStaticTexts,
} from '@/types/category/categoryTypes';
import { AnimationEffect, ImageType } from '@/types/gameTypes';

import styles from './ModularGridGameTile.module.scss';

interface ModularGridGameTileProps {
  gameDetailsInfo?: boolean;
  gameInfoTooltip?: string;
  game?: GameTileProps;
  onClick?: (game: GameClickProps) => void;
  onInfoClick?: (game: GameClickProps, path: string) => void;
  outfit: QuicksilverTileOutfitType;
  isLazy?: boolean;
  isAnchor?: boolean;
  ghost?: boolean;
  tileSize?: 'small' | 'large';
  categoryAttributes?: CategoryFinancialWebAttributes;
  renderExtendedInfo?: (
    game: GameTileProps,
    staticTexts?: Pick<CategoryStaticTexts, 'bingoTimer'>
  ) => JSX.Element | null;
  staticTexts?: Pick<CategoryStaticTexts, 'bingoTimer'>;
  shouldShowSash?: boolean;
  isIntersectionObserverDisabled?: boolean;
}

export const prepareImages = (
  gameImage: ImageType,
  tileSize: 'small' | 'large'
) => {
  return tileSize === 'small'
    ? IMAGE_SIZES.small.map(config => ({
        min: config.min,
        max: config.max,
        src: transformImage(gameImage?.source.base, config.size),
      }))
    : IMAGE_SIZES.large.map(config => ({
        min: config.min,
        max: config.max,
        src: transformImage(gameImage?.source.base, config.size),
      }));
};

export const getSizedSources = (
  sizedImages: GameTileProps['sizedImages'],
  image: ImageType,
  tileSize: 'small' | 'large'
) => {
  return sizedImages
    ? sizedImages[tileSize === 'small' ? 'smallTile' : 'largeTile']
    : prepareImages(image, tileSize);
};

const handleGameTileClick = (
  e: React.MouseEvent<HTMLAnchorElement>,
  onClick: ModularGridGameTileProps['onClick'],
  game: GameTileProps,
  preventDefaultForGameClick: boolean
) => {
  if (preventDefaultForGameClick) e.preventDefault();
  onClick && onClick(prepareGameClickProps(game));
};

const handleGameTileInfoIconClick = (
  e: React.MouseEvent<HTMLDivElement>,
  onInfoClick: ModularGridGameTileProps['onInfoClick'],
  game: GameTileProps,
  gameDetailsPath: string
) => {
  e.preventDefault();
  onInfoClick && onInfoClick(prepareGameClickProps(game), gameDetailsPath);
};

export const prepareGameClickProps = (game: GameTileProps) => ({
  id: game.id,
  title: game.title,
  name: game.name,
  provider: game.provider,
  integration: game.integration,
  presentation_code: game.presentation_code,
  transaction_code: game.transaction_code,
  transactional_game_code: game.transactional_game_code,
  launchUrl: game.launchUrl,
  gameDetailsPath: game.gameDetailsPath,
  gameLaunchPath: game.gameLaunchPath,
  type: game.type,
  trackingTitle: game.tracking_metadata?.title || game.title,
  sash: game.sash,
});

const ModularGridGameTile: React.FC<ModularGridGameTileProps> = ({
  game,
  onClick,
  onInfoClick,
  outfit,
  isLazy = false,
  isAnchor = false,
  ghost,
  gameDetailsInfo,
  gameInfoTooltip,
  tileSize = 'small',
  renderExtendedInfo,
  categoryAttributes,
  staticTexts,
  shouldShowSash = true,
  isIntersectionObserverDisabled = false,
}) => {
  const { loggedIn } = React.useContext(UserSessionContext);

  if (ghost) {
    return (
      <div className={styles.ghostTileContainer}>
        <GhostTile />
      </div>
    );
  }
  if (!game) return null;

  const preventDefaultForGameClick = ShouldPreventDefaultForClick(
    game.integration
  );

  const preparedGameInfoTooltip = prepareGameInfoTooltip(
    gameInfoTooltip,
    game.title
  );

  const validateLayerAnimation = (animation: GameTileProps['animation']) => {
    if (!animation || !animation.animationType) return false;
    if (
      animation.animationType === AnimationEffect.sparkleLoop &&
      !animation.asset
    )
      return false;
    return true;
  };

  const sizedImages =
    loggedIn && game?.sizedImagesLoggedIn
      ? game?.sizedImagesLoggedIn
      : game?.sizedImages;
  const gameImage =
    loggedIn && game?.image_logged_in ? game?.image_logged_in : game?.image;

  return (
    <IntersectionObserverWrapper
      className={styles.container}
      loadingClassName={styles.loadingWrapper}
      disabled={isIntersectionObserverDisabled}
      dataTestId="game-tile-wrapper"
    >
      <GameTile data-testid="game-tile" attachAnimationStyles={game?.animation}>
        <LayersWrapper rounded={renderExtendedInfo ? 'top' : 'all'}>
          {validateLayerAnimation(game.animation) ? (
            <AnimatedAssetLayer
              game={game}
              data-testid="animated-game-tile"
              isLazy={isLazy}
              tileSize={tileSize}
            />
          ) : (
            <AssetLayer
              type="image"
              sizedSources={getSizedSources(sizedImages, gameImage, tileSize)}
              loadingAnimation={game.loading}
              data-testid="picture-layer"
              imageSrc={gameImage.source}
              alt={gameImage.alt || ''}
              rounded={renderExtendedInfo ? 'top' : 'all'}
            />
          )}
        </LayersWrapper>
        {isAnchor && game.launchUrl && (
          <GameLink
            url={game.launchUrl}
            categoryAttributes={categoryAttributes}
            target="_blank"
            onClick={e =>
              handleGameTileClick(e, onClick, game, preventDefaultForGameClick)
            }
            notIndexed={game.notIndexed}
            integration={game.integration}
          ></GameLink>
        )}
        {gameDetailsInfo && (
          <a
            className={cx(styles.info, styles.link)}
            target="_blank"
            rel="noopener noreferrer"
            href={game.gameDetailsPath}
            title={preparedGameInfoTooltip}
          >
            <div
              className={cx(
                styles.infoWrapper,
                tileSize === 'large' && styles.infoWrapperWithLabel
              )}
              onClick={e =>
                handleGameTileInfoIconClick(
                  e,
                  onInfoClick,
                  game,
                  game.gameDetailsPath
                )
              }
            >
              <Info altText={preparedGameInfoTooltip} />
            </div>
          </a>
        )}
        {game.table && <LiveTableLayer table={game.table} outfit={outfit} />}
        {game.jackpot && !game.table && outfit !== SMALL_OUTFIT && (
          <JackpotLayerGameTile {...game.jackpot} />
        )}
        {shouldShowSash && game.sash && (
          <SashLayer
            sash={game.sash.title}
            outfit={outfit}
            textColor={game.sash.text_color}
            textBackgroundColor={game.sash.text_background_color}
            background={game.sash.background}
          />
        )}
      </GameTile>
      {renderExtendedInfo && renderExtendedInfo(game, staticTexts)}
    </IntersectionObserverWrapper>
  );
};

export default ModularGridGameTile;
